import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardBody } from 'reactstrap';
import { faCamera, faSpiderWeb } from '@fortawesome/pro-light-svg-icons';
import { Icon, IconStack } from 'js/components';
import OrgTrends from 'js/routes/Organizations/components/OrgComponent/Visualizations/OrgTrends';
import { buildSnapshotTrendsPickerPath } from './org-snapshot-utils';

function TrendsEmpty({ snapshotCount, createPath }) {
  return (
    <div className="media text-muted mt-4 mb-4">
      <IconStack size="4x" fixedWidth className="mr-3">
        <Icon icon={faCamera} />
        <Icon icon={faSpiderWeb} transform="shrink-12 down-1" />
      </IconStack>
      <div className="media-body">
        {snapshotCount === 0 ? (
          <>
            <p>You don&apos;t have any snapshot trends to show.</p>
            <p>
              Create a snapshot to freeze this assessment&apos;s combined scores
              at a point in time, then create another to see a trend.{' '}
              <Link to={createPath}>Create a snapshot</Link>
            </p>
          </>
        ) : (
          <>
            <p>You need another snapshot to see a trend.</p>
            <p>
              Create another snapshot to freeze this assessment&apos;s combined
              scores at a later point in time.{' '}
              <Link to={createPath}>Create a snapshot</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function OrgSnapshotTrends({
  organization,
  workspace,
  assessmentTemplate = null,
  compareDocument = null,
  compareSnapshots = [],
  isCompareLoading = false,
  compareError = null,
  createPath,
}) {
  const { workspaceId, orgId, sourceType, sourceKey } = useParams();
  const decodedKey = useMemo(() => decodeURIComponent(sourceKey), [sourceKey]);

  const snapshotCompare = useMemo(
    () => ({
      document: compareDocument,
      snapshots: compareSnapshots,
    }),
    [compareDocument, compareSnapshots]
  );

  const buildPickerPath = useMemo(
    () =>
      ({ subOrgId, teamId }) =>
        buildSnapshotTrendsPickerPath({
          workspaceId,
          orgId,
          sourceType,
          sourceKey: decodedKey,
          subOrgId,
          teamId,
        }),
    [decodedKey, orgId, sourceType, workspaceId]
  );

  if (compareError) {
    return (
      <Card>
        <CardBody>Sorry, there was a problem loading the details.</CardBody>
      </Card>
    );
  }

  if (!isCompareLoading && compareSnapshots.length < 2) {
    return (
      <Card>
        <CardBody>
          <TrendsEmpty
            snapshotCount={compareSnapshots.length}
            createPath={createPath}
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <OrgTrends
      snapshotCompare={snapshotCompare}
      assessmentTemplate={assessmentTemplate}
      assessmentKey={decodedKey}
      organization={organization}
      workspace={workspace}
      loading={isCompareLoading}
      error={compareError}
      buildPickerPath={buildPickerPath}
      debounceTrendPoints={false}
    />
  );
}

export default OrgSnapshotTrends;
